import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useAuth, useAuthOptional } from "@/contexts/AuthContext";
import {
  readWorkspaceOrgIdFromStorage,
  writeWorkspaceOrgIdToStorage,
  WORKSPACE_ORG_STORAGE_KEY,
} from "@/lib/workspaceOrgStorage";
import { isUuidLike, readDevImpersonation } from "@/lib/devImpersonationStorage";

export type WorkspaceOrgContextValue = {
  /** Org id used across the app (admin target, KB, org settings, GHL, sync). */
  workspaceOrganizationId: string | null;
  setWorkspaceOrganizationId: (id: string | null) => void;
};

const WorkspaceOrgContext = createContext<WorkspaceOrgContextValue | null>(null);

function canUseWorkspaceOrgOverride(
  orgId: string,
  memberOrganizationIds: string[],
  hasPlatformWorkspaceOrg: boolean,
  devBrowseAllOrgs: boolean,
  realUserIsPlatformDev: boolean
): boolean {
  if (memberOrganizationIds.includes(orgId)) return true;
  if (hasPlatformWorkspaceOrg) return true;
  if (devBrowseAllOrgs && realUserIsPlatformDev && isUuidLike(orgId)) {
    return true;
  }
  return false;
}

function linkOrgFromLocation(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const org = new URLSearchParams(window.location.search).get("org")?.trim() ?? "";
    return isUuidLike(org) ? org : null;
  } catch {
    return null;
  }
}

export function WorkspaceOrgProvider(props: { children: ReactNode }) {
  const auth = useAuth();
  const [storedOverride, setStoredOverride] = useState<string | null>(
    () => linkOrgFromLocation() ?? readWorkspaceOrgIdFromStorage(),
  );

  const memberOrganizationIds = auth.memberOrganizationIds ?? [];
  const hasPlatformWorkspaceOrg = auth.hasRole("platform_admin", "platform_dev", "platform_trainer");
  const realPlatformDev = auth.realHasRole("platform_dev");

  const validate = useCallback(
    (id: string | null) => {
      if (!id) return false;
      const imp = readDevImpersonation();
      return canUseWorkspaceOrgOverride(
        id,
        memberOrganizationIds,
        hasPlatformWorkspaceOrg,
        imp.enabled && imp.workspaceBrowseAllOrgs,
        realPlatformDev
      );
    },
    [memberOrganizationIds, hasPlatformWorkspaceOrg, realPlatformDev]
  );

  useEffect(() => {
    const linked = linkOrgFromLocation();
    if (!linked) return;
    writeWorkspaceOrgIdToStorage(linked);
  }, []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== WORKSPACE_ORG_STORAGE_KEY) return;
      const next = e.newValue && e.newValue.trim() ? e.newValue.trim() : null;
      setStoredOverride(next);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (auth.status !== "ready" || !storedOverride) return;
    if (!validate(storedOverride)) {
      setStoredOverride(null);
      writeWorkspaceOrgIdToStorage(null);
    }
  }, [auth.status, storedOverride, validate]);

  const setWorkspaceOrganizationId = useCallback((id: string | null) => {
    const next = id && id.trim() ? id.trim() : null;
    setStoredOverride(next);
    writeWorkspaceOrgIdToStorage(next);
  }, []);

  const workspaceOrganizationId = useMemo(() => {
    const base = auth.activeOrganizationId;
    if (storedOverride && validate(storedOverride)) return storedOverride;
    return base;
  }, [auth.activeOrganizationId, storedOverride, validate]);

  const value = useMemo(
    () => ({ workspaceOrganizationId, setWorkspaceOrganizationId }),
    [workspaceOrganizationId, setWorkspaceOrganizationId]
  );

  return <WorkspaceOrgContext.Provider value={value}>{props.children}</WorkspaceOrgContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useWorkspaceOrg(): WorkspaceOrgContextValue {
  const ctx = useContext(WorkspaceOrgContext);
  if (!ctx) throw new Error("useWorkspaceOrg must be used within WorkspaceOrgProvider");
  return ctx;
}

/** Workspace org when `WorkspaceOrgProvider` is mounted; otherwise profile-derived active org. */
// eslint-disable-next-line react-refresh/only-export-components
export function useScopedOrganizationId(): string | null {
  const ws = useContext(WorkspaceOrgContext);
  const auth = useAuthOptional();
  return ws?.workspaceOrganizationId ?? auth?.activeOrganizationId ?? null;
}